import { ProjectScopedRedirect } from './ProjectScopedRedirect';
import {
  BuildDetailPage,
  ExecutionRunDetailPage,
  ExecutionRunsPage,
  FeatureDetailPage,
  FeaturesPage,
  JobDetailPage,
  JobRunDetailPage,
  JobsPage,
  PipelineDetailPage,
  PipelinesPage,
  ReleaseDetailPage,
  ReleasesPage,
  ResultsFileDetailPage,
  StageDetailPage,
  StageExecutionDetailPage,
  StagesPage,
  SuiteDetailPage,
  SuiteRunDetailPage,
  SuitesPage,
  TestDetailPage,
  TestRunDetailPage,
  TestsPage,
  UploadsPage,
  UploadDetailPage,
} from './routePages';
import type { AppRouteDefinition } from './routes';

const projectScopedRoutePath = (path: string): string =>
  `/workspaces/:workspaceSlug/projects/:projectSlug${path}`;

export const projectScopedProductRouteDefinitions = [
  {
    key: 'workspace-project-releases',
    path: projectScopedRoutePath('/releases'),
    title: 'Releases',
    requiresAuth: true,
    featureFlag: 'releases',
    element: () => <ReleasesPage />,
  },
  {
    key: 'workspace-project-features',
    path: projectScopedRoutePath('/features'),
    title: 'Features',
    requiresAuth: true,
    featureFlag: 'features',
    element: () => <FeaturesPage />,
  },
  {
    key: 'workspace-project-execution-runs',
    path: projectScopedRoutePath('/execution-runs'),
    title: 'Execution Runs',
    description:
      'View execution runs, success rates, status trends, and upload outcomes across your project.',
    requiresAuth: true,
    element: () => <ExecutionRunsPage />,
  },
  {
    key: 'workspace-project-uploads',
    path: projectScopedRoutePath('/uploads'),
    title: 'Uploads',
    requiresAuth: true,
    element: () => <UploadsPage />,
  },
  {
    key: 'workspace-project-upload-detail',
    path: projectScopedRoutePath('/uploads/:id'),
    title: 'Upload',
    requiresAuth: true,
    element: ({ session }) => <UploadDetailPage session={session} />,
  },
  {
    key: 'workspace-project-execution-run-detail',
    path: projectScopedRoutePath('/execution-runs/:id'),
    title: 'Execution Run',
    requiresAuth: true,
    element: ({ session }) => <ExecutionRunDetailPage session={session} />,
  },
  {
    key: 'workspace-project-suites',
    path: projectScopedRoutePath('/suites'),
    title: 'Suites',
    requiresAuth: true,
    element: () => <SuitesPage />,
  },
  {
    key: 'workspace-project-tests',
    path: projectScopedRoutePath('/tests'),
    title: 'Tests',
    requiresAuth: true,
    element: () => <TestsPage />,
  },
  {
    key: 'workspace-project-pipelines',
    path: projectScopedRoutePath('/pipelines'),
    title: 'Pipelines',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: () => <PipelinesPage />,
  },
  {
    key: 'workspace-project-stages',
    path: projectScopedRoutePath('/stages'),
    title: 'Stages',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: () => <StagesPage />,
  },
  {
    key: 'workspace-project-jobs',
    path: projectScopedRoutePath('/jobs'),
    title: 'Jobs',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: () => <JobsPage />,
  },
  {
    key: 'workspace-project-stage-run-detail',
    path: projectScopedRoutePath('/stages/:stageSlug/runs/:stageExecutionId'),
    title: 'Stage Run',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <StageExecutionDetailPage session={session} />,
  },
  {
    key: 'workspace-project-stage-detail',
    path: projectScopedRoutePath('/stages/:stageSlug'),
    title: 'Stage',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <StageDetailPage session={session} />,
  },
  {
    key: 'workspace-project-job-run-detail',
    path: projectScopedRoutePath('/jobs/:jobSlug/runs/:jobRunId'),
    title: 'Job Run',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <JobRunDetailPage session={session} />,
  },
  {
    key: 'workspace-project-job-detail',
    path: projectScopedRoutePath('/jobs/:jobSlug'),
    title: 'Job',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <JobDetailPage session={session} />,
  },
  {
    key: 'workspace-project-pipeline-detail',
    path: projectScopedRoutePath('/pipelines/:pipelineSlug'),
    title: 'Pipeline',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <PipelineDetailPage session={session} />,
  },
  {
    key: 'workspace-project-build-detail',
    path: projectScopedRoutePath('/pipelines/:pipelineSlug/runs/:runNumber'),
    title: 'Job',
    requiresAuth: true,
    featureFlag: 'pipelines',
    element: ({ session }) => <BuildDetailPage session={session} />,
  },
  {
    key: 'workspace-project-results-files-redirect',
    path: projectScopedRoutePath('/results-files'),
    title: 'Results Files',
    requiresAuth: true,
    element: () => <ProjectScopedRedirect path="/execution-runs" hash="#results-files" />,
  },
  {
    key: 'workspace-project-results-file-detail',
    path: projectScopedRoutePath('/results-files/:slug'),
    title: 'Results Files',
    requiresAuth: true,
    element: ({ session }) => <ResultsFileDetailPage session={session} />,
  },
  {
    key: 'workspace-project-suite-run-detail',
    path: projectScopedRoutePath('/suites/:suiteSlug/runs/:runNumber'),
    title: 'Suite Run',
    requiresAuth: true,
    element: ({ session }) => <SuiteRunDetailPage session={session} />,
  },
  {
    key: 'workspace-project-test-run-detail',
    path: projectScopedRoutePath('/tests/:testSlug/runs/:runNumber'),
    title: 'Test Run',
    requiresAuth: true,
    element: ({ session }) => <TestRunDetailPage session={session} />,
  },
  {
    key: 'workspace-project-feature-detail',
    path: projectScopedRoutePath('/features/:slug'),
    title: 'Feature',
    requiresAuth: true,
    featureFlag: 'features',
    element: ({ session }) => <FeatureDetailPage session={session} />,
  },
  {
    key: 'workspace-project-release-detail',
    path: projectScopedRoutePath('/releases/:slug'),
    title: 'Release',
    requiresAuth: true,
    featureFlag: 'releases',
    element: ({ session }) => <ReleaseDetailPage session={session} />,
  },
  {
    key: 'workspace-project-suite-detail',
    path: projectScopedRoutePath('/suites/:slug'),
    title: 'Suite',
    requiresAuth: true,
    element: ({ session }) => <SuiteDetailPage session={session} />,
  },
  {
    key: 'workspace-project-test-detail',
    path: projectScopedRoutePath('/tests/:slug'),
    title: 'Test',
    requiresAuth: true,
    element: ({ session }) => <TestDetailPage session={session} />,
  },
] satisfies AppRouteDefinition[];
